/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Toaster Component
 * ============================================================================
 *
 * Global toast notification container that renders notifications from
 * the notification store. Add this component once at the app root.
 *
 * ============================================================================
 */

import React from 'react';
import { Toast, ToastViewport, ToastPrimitive } from './Toast';
import { useNotificationStore } from '@/stores/notificationStore';

/**
 * Toaster - Global toast notification container
 *
 * Usage:
 * 1. Add <Toaster /> to your app root (e.g., in main.tsx or App.tsx)
 * 2. Use useNotification() hook to show notifications from anywhere
 *
 * @example
 * // In main.tsx or App.tsx:
 * <App />
 * <Toaster />
 *
 * // In any component:
 * const { success, error } = useNotification();
 * success('Operation completed!');
 * error('Something went wrong', 'Error Title', { email: 'Already exists' });
 */
export const Toaster: React.FC = () => {
  const { notifications, removeNotification } = useNotificationStore();

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          id={notification.id}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          duration={notification.duration}
          onClose={removeNotification}
        />
      ))}
      <ToastViewport />
    </ToastPrimitive.Provider>
  );
};

export default Toaster;
